import React from 'react';
import { ReadOnlyItem, Text } from '@ydistri/ds';
import { SkuRedistributionPaneType } from '../../../../../redistributionTypes';
import { styled } from 'styled-components';
import { Popover } from 'antd';
import { formatNumber } from '@ydistri/generic-utils';
import { NO_DATA_PLACEHOLDER } from '../../../../../../../../components/global/NoData/NoData';

interface SkuExecutionResultProps {
  pickingPositionType: SkuRedistributionPaneType;
  quantityRequested: number;
  quantityExecuted?: number | null;
  resultUnknown?: boolean;
  loading?: boolean;
}

/* eslint-disable @typescript-eslint/naming-convention -- we are going to use SkuRedistributionPaneType */
const labels: Record<SkuRedistributionPaneType, string> = {
  Source: 'Shipped',
  Target: 'Delivered',
};
/* eslint-enable @typescript-eslint/naming-convention -- normal rules after this */

const SkuExecutionResultWrapper = styled.div`
  margin-left: auto;
  min-width: 10rem;
`;

const UNKNOWN_RESULT = 'Unknown';

const getResultStatusText = (
  quantityRequested: number,
  quantityExecuted: number,
  resultUnknown: boolean,
  loading: boolean,
) => {
  if (resultUnknown) {
    return `\u2753 ${UNKNOWN_RESULT}`;
  } else if (loading) {
    return NO_DATA_PLACEHOLDER;
  } else if (quantityExecuted >= quantityRequested) {
    return '\u2705 Yes'; //check mark symbol
  } else if (quantityExecuted === 0) {
    return '\u26d4 No'; //red cross mark symbol
  } else {
    return '\u274c Partially'; //red cross mark symbol
  }
};

const getExecutedQuantityText = (
  quantityExecuted: number,
  resultUnknown: boolean,
  loading: boolean,
) => {
  if (resultUnknown) {
    return UNKNOWN_RESULT;
  }

  if (loading) {
    return NO_DATA_PLACEHOLDER;
  }

  return formatNumber(quantityExecuted, 0);
};

/**
 * Display information how was the requirement for quantity fulfilled.
 * Compares the quantity requested and fulfilled and outputs Yes/No/Partially
 * To be displayed in a flexbox row container, will be aligned to the right.
 * @param pickingPositionType Is this information for the Source or Target pane?
 * @param quantityExecuted quantity actually executed
 * @param quantityRequested quantity originally requested by redistribution
 * @param resultUnknown cannot determine the execution result
 * @param loading execution result is still being loaded
 */
const SkuExecutionResult: React.FC<SkuExecutionResultProps> = ({
  pickingPositionType,
  quantityExecuted,
  quantityRequested,
  resultUnknown = false,
  loading = false,
}) => {
  const effectiveExecutedQuantity = quantityExecuted ?? 0;
  const shippedQuantityText = getResultStatusText(
    quantityRequested,
    effectiveExecutedQuantity,
    resultUnknown,
    loading,
  );
  const executedQuantityText = getExecutedQuantityText(
    effectiveExecutedQuantity,
    resultUnknown,
    loading,
  );
  const popoverContent = `${executedQuantityText} / ${formatNumber(quantityRequested, 0)}`;

  return (
    <SkuExecutionResultWrapper>
      <ReadOnlyItem label={labels[pickingPositionType]}>
        <Popover content={popoverContent} title="Quantity executed vs. requested">
          <Text>{shippedQuantityText}</Text>
        </Popover>
      </ReadOnlyItem>
    </SkuExecutionResultWrapper>
  );
};

export default SkuExecutionResult;
